import { t } from "@lingui/macro";

/**
 * Maps a section to the item fields the builder's entry form reads/writes, plus the
 * human labels for that section. Extracted from builder-redesign so it can be unit-tested.
 *
 * Spec 046 fix: custom sections (`custom.<id>`) were absent here, so `getSectionFieldMap`
 * returned `undefined` and `EntrySectionForm` rendered `null` — a custom section showed an
 * empty body with no way to add items. Custom items use the CustomSection schema
 * (name / description / location), so they map like a generic entry.
 */

export type FieldMap = {
  title: string;
  subtitle: string;
  location?: string;
  titleLabel: string;
  subtitleLabel: string;
  addLabel: string;
};

export const SECTION_FIELD_KEYS: Record<
  string,
  { title: string; subtitle: string; location?: string }
> = {
  experience: { title: "position", subtitle: "company", location: "location" },
  education: { title: "studyType", subtitle: "institution", location: "area" },
  volunteer: { title: "position", subtitle: "organization", location: "location" },
  projects: { title: "name", subtitle: "description" },
  publications: { title: "name", subtitle: "publisher" },
  certifications: { title: "name", subtitle: "issuer" },
  awards: { title: "title", subtitle: "awarder" },
  references: { title: "name", subtitle: "description" },
};

type LabelTriple = { titleLabel: string; subtitleLabel: string; addLabel: string };

export const isCustomSectionKey = (sectionKey: string): boolean => sectionKey.startsWith("custom.");

export const getSectionFieldMap = (sectionKey: string): FieldMap | undefined => {
  // Custom sections render through the same entry form; their items carry name/description/location.
  if (isCustomSectionKey(sectionKey)) {
    return {
      title: "name",
      subtitle: "description",
      location: "location",
      titleLabel: t`Name`,
      subtitleLabel: t`Description`,
      addLabel: t`Add item`,
    };
  }

  const keys = (
    SECTION_FIELD_KEYS as Partial<
      Record<string, { title: string; subtitle: string; location?: string }>
    >
  )[sectionKey];
  if (!keys) return undefined;

  const labelMap: Partial<Record<string, LabelTriple>> = {
    experience: { titleLabel: t`Role`, subtitleLabel: t`Company`, addLabel: t`Add experience` },
    education: { titleLabel: t`Degree`, subtitleLabel: t`Institution`, addLabel: t`Add education` },
    volunteer: {
      titleLabel: t`Role`,
      subtitleLabel: t`Organization`,
      addLabel: t`Add volunteering`,
    },
    projects: { titleLabel: t`Project`, subtitleLabel: t`Description`, addLabel: t`Add project` },
    publications: {
      titleLabel: t`Title`,
      subtitleLabel: t`Publisher`,
      addLabel: t`Add publication`,
    },
    certifications: {
      titleLabel: t`Certificate`,
      subtitleLabel: t`Issuer`,
      addLabel: t`Add certification`,
    },
    awards: { titleLabel: t`Award`, subtitleLabel: t`Awarder`, addLabel: t`Add award` },
    references: { titleLabel: t`Name`, subtitleLabel: t`Description`, addLabel: t`Add reference` },
  };
  const labels = labelMap[sectionKey];
  if (!labels) return undefined;
  return { ...keys, ...labels };
};
